/**
 * Schema-driven Configuration editor (#170 - consumer side of #168).
 *
 * Reads ``configuration_setting_catalog_tbl`` via
 * ``apiClient.listConfigurationSettings()`` and renders one input
 * control per setting whose ``applicable_scopes`` includes this
 * tier. Each ``value_type`` maps to a type-aware control with
 * inline validation:
 *
 *   email / phone / url / text_short / text_long → text inputs
 *     (with the same validators ProvidersTab + InvitePersonDialog use)
 *   hex_color → BrandEditor's <input type="color"> + HSL-triple sync
 *   timezone  → CURATED_TIMEZONES dropdown (+ browser-detect)
 *   boolean   → Toggle component
 *   integer   → numeric input with optional min/max from value_schema
 *   select    → dropdown sourced from value_schema.options[]
 *   tipping_rule → JSON fallback (full structured editor in a follow-up)
 *
 * The component is read-only when ``canWrite=false`` (caller's RBAC
 * predicate). Writes flow through the caller's ``onSave`` - this
 * component doesn't know which patch endpoint to hit, so the same
 * editor works for partner / org / location.
 *
 * Settings the catalog declares but the consumer hasn't populated
 * appear with the catalog's ``description`` as helper text and a
 * placeholder cue. Settings the consumer has set but the catalog
 * has retired (``is_active=false``) or that don't apply to this
 * tier render at the bottom in a "legacy" group with a clear-only
 * affordance - never silently strip user data.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { Toggle } from "@/components/Toggle";
import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { hexToHslTriple, hslTripleToHex, isHslTriple } from "@/lib/brand-color";
import { timezoneChoicesIncluding } from "@/lib/timezones";
import { validateEmail, validatePhone } from "@/lib/validation";
import type { ConfigurationSetting, ConfigurationSettingScope } from "@tilt/admin-sdk";

export interface ConfigurationEditorProps {
  readonly tier: ConfigurationSettingScope;
  /** Current value of the entity's ``config`` JSONB. */
  readonly value: Record<string, unknown>;
  /** Saves a fresh ``config`` blob (caller picks the right endpoint). */
  readonly onSave: (next: Record<string, unknown>) => Promise<unknown>;
  /** Disables every input + the Save button when false. RBAC predicate
   *  lives at the caller (it knows the entity + caller's role grants). */
  readonly canWrite: boolean;
  /** Optional override - defaults to "tilt-pos". The catalog's
   *  ``consumer_app`` filter narrows the list so the org Configuration
   *  tab doesn't surface admin-only settings. */
  readonly consumerApp?: string;
}

const URL_RE = /^https?:\/\/\S+$/i;

export function ConfigurationEditor(props: ConfigurationEditorProps): JSX.Element {
  const { tier, value, onSave, canWrite, consumerApp } = props;
  const targetApp = consumerApp ?? "tilt-pos";

  const settingsQuery = useQuery({
    queryKey: ["configuration-settings"],
    queryFn: () => apiClient.listConfigurationSettings(),
    staleTime: 60_000,
  });

  // Re-seed local draft state every time the parent's ``value`` ref
  // changes (e.g. on save success the parent re-fetches and hands us
  // the canonical blob). Without this useEffect, the caller's
  // optimistic refetch would still display the stale typed values.
  const [draft, setDraft] = useState<Record<string, unknown>>(value);
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () => onSave(draft),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err: unknown) => setError(describe(err)),
  });

  // Catalog rows that apply HERE: active, in this consumer app, and
  // ``applicable_scopes`` includes ``tier``. Sorted by the catalog's
  // ``sort_order`` so super_admin controls the order in one place.
  const applicable = useMemo(
    () =>
      (settingsQuery.data?.settings ?? [])
        .filter((s) => s.is_active)
        .filter((s) => !s.consumer_app || s.consumer_app === targetApp)
        .filter((s) => s.applicable_scopes.includes(tier))
        .slice()
        .sort(
          (a, b) => a.sort_order - b.sort_order || a.display_name.localeCompare(b.display_name),
        ),
    [settingsQuery.data, targetApp, tier],
  );

  // Anything in the consumer's ``config`` that the catalog doesn't
  // recognise (or has since retired). Surface separately so we never
  // silently drop the admin's prior data on save.
  const knownSlugs = useMemo(() => new Set(applicable.map((s) => s.slug)), [applicable]);
  const legacyKeys = useMemo(
    () => Object.keys(draft).filter((k) => !knownSlugs.has(k)),
    [draft, knownSlugs],
  );

  if (settingsQuery.isPending) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading settings catalog…
      </div>
    );
  }
  if (settingsQuery.isError) {
    return (
      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
        Couldn&apos;t load the settings catalog: {describe(settingsQuery.error)}
      </div>
    );
  }

  const setSlug = (slug: string, next: unknown): void => {
    setDraft((prev) => {
      const out = { ...prev };
      if (next === null || next === undefined || next === "") {
        delete out[slug];
      } else {
        out[slug] = next;
      }
      return out;
    });
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(value);

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4 shadow-sm sm:p-5">
      {applicable.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No settings in the catalog apply to this {tier}. Ask a platform admin to add one on the
          Catalog → Settings tab.
        </p>
      ) : (
        <ul className="divide-y">
          {applicable.map((s) => (
            <li key={s.id} className="py-3 first:pt-0 last:pb-0">
              <SettingControl
                setting={s}
                value={draft[s.slug]}
                onChange={(next) => setSlug(s.slug, next)}
                disabled={!canWrite}
              />
            </li>
          ))}
        </ul>
      )}

      {legacyKeys.length > 0 && (
        <details className="rounded-md border border-amber-500/30 bg-amber-50/40 p-3 text-xs dark:bg-amber-950/20">
          <summary className="cursor-pointer text-amber-900 dark:text-amber-200">
            {legacyKeys.length} legacy setting{legacyKeys.length === 1 ? "" : "s"} stored on this{" "}
            {tier}
          </summary>
          <p className="mt-2 text-muted-foreground">
            These keys are present on this {tier}&apos;s config but the platform catalog no longer
            defines them. They&apos;re kept verbatim on save; clear individually to drop them.
          </p>
          <ul className="mt-2 space-y-1.5">
            {legacyKeys.map((k) => (
              <li key={k} className="flex items-center justify-between gap-2">
                <code className="rounded bg-muted px-1.5 py-0.5">{k}</code>
                <span className="truncate font-mono text-[10px] text-muted-foreground">
                  {JSON.stringify(draft[k])}
                </span>
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => setSlug(k, null)}
                    className="rounded border border-destructive/30 bg-destructive/5 px-2 py-0.5 text-[10px] text-destructive hover:bg-destructive/10"
                  >
                    Clear
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">{error}</div>
      )}

      {canWrite && (
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
          {dirty && !save.isPending && (
            <button
              type="button"
              onClick={() => setDraft(value)}
              className="rounded-md border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Discard
            </button>
          )}
          {saved && <span className="text-xs text-emerald-600">Saved.</span>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- //
// Per-row control                                                  //
// ---------------------------------------------------------------- //

interface SettingControlProps {
  readonly setting: ConfigurationSetting;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly disabled: boolean;
}

function SettingControl(props: SettingControlProps): JSX.Element {
  const { setting, value, onChange, disabled } = props;
  const labelId = `cfg-${setting.slug}`;
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-baseline gap-2">
        <label htmlFor={labelId} className="text-sm font-medium text-foreground">
          {setting.display_name}
        </label>
        <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
          {setting.slug}
        </code>
        <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">
          {setting.value_type}
        </span>
      </div>
      {setting.description && (
        <p className="text-xs text-muted-foreground">{setting.description}</p>
      )}
      <ValueInput
        id={labelId}
        setting={setting}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

interface ValueInputProps {
  readonly id: string;
  readonly setting: ConfigurationSetting;
  readonly value: unknown;
  readonly onChange: (next: unknown) => void;
  readonly disabled: boolean;
}

function ValueInput(props: ValueInputProps): JSX.Element {
  const { id, setting, value, onChange, disabled } = props;
  const baseInput =
    "w-full rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60";

  switch (setting.value_type) {
    case "email":
    case "phone":
    case "url":
    case "text_short":
      return (
        <TextInput
          id={id}
          setting={setting}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          disabled={disabled}
          className={baseInput}
        />
      );
    case "text_long":
      return (
        <textarea
          id={id}
          rows={4}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          maxLength={resolveNumber(setting.value_schema, "max_length") ?? 4000}
          className={baseInput}
        />
      );
    case "boolean":
      return (
        <Toggle
          checked={value === true}
          onChange={(checked) => onChange(checked)}
          disabled={disabled}
          label={value === true ? "On" : "Off"}
        />
      );
    case "integer": {
      const min = resolveNumber(setting.value_schema, "min");
      const max = resolveNumber(setting.value_schema, "max");
      return (
        <input
          id={id}
          type="number"
          inputMode="numeric"
          step={1}
          {...(min !== null ? { min } : {})}
          {...(max !== null ? { max } : {})}
          value={typeof value === "number" ? String(value) : ""}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const n = Number.parseInt(raw, 10);
            return onChange(Number.isFinite(n) ? n : null);
          }}
          disabled={disabled}
          className={baseInput}
        />
      );
    }
    case "select": {
      const options = resolveOptions(setting.value_schema);
      return (
        <select
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={baseInput}
        >
          <option value="">- not set -</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    case "timezone": {
      const current = typeof value === "string" ? value : "";
      const choices = timezoneChoicesIncluding(current ? [current] : []);
      return (
        <select
          id={id}
          value={current}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
          className={baseInput}
        >
          <option value="">- not set -</option>
          {choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      );
    }
    case "hex_color":
      return (
        <HexColorInput
          id={id}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          disabled={disabled}
          className={baseInput}
        />
      );
    case "tipping_rule":
      return (
        <textarea
          id={id}
          rows={5}
          value={value == null ? "" : JSON.stringify(value, null, 2)}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === "") return onChange(null);
            try {
              return onChange(JSON.parse(raw));
            } catch {
              // Keep the raw text in local state by ignoring the
              // change; the caller's draft retains the prior value.
              // A structured tipping-rule UI lands in a follow-up.
              return undefined;
            }
          }}
          disabled={disabled}
          spellCheck={false}
          className={`${baseInput} font-mono text-xs`}
        />
      );
  }
}

function TextInput(props: {
  id: string;
  setting: ConfigurationSetting;
  value: string;
  onChange: (next: unknown) => void;
  disabled: boolean;
  className: string;
}): JSX.Element {
  const { id, setting, value, onChange, disabled, className } = props;
  const [touched, setTouched] = useState(false);

  let validationError: string | null = null;
  if (touched && value.trim() !== "") {
    if (setting.value_type === "email") {
      const r = validateEmail(value);
      if (!r.ok) validationError = r.error ?? "Invalid email.";
    } else if (setting.value_type === "phone") {
      const r = validatePhone(value);
      if (!r.ok) validationError = r.error ?? "Invalid phone.";
    } else if (setting.value_type === "url") {
      if (!URL_RE.test(value.trim())) validationError = "Enter a full URL (https://…).";
    }
  }

  const maxLen = resolveNumber(setting.value_schema, "max_length");
  return (
    <div className="space-y-1">
      <input
        id={id}
        type={setting.value_type === "email" ? "email" : "text"}
        inputMode={setting.value_type === "phone" ? "tel" : undefined}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        disabled={disabled}
        {...(maxLen !== null ? { maxLength: maxLen } : {})}
        className={className}
      />
      {validationError && <p className="text-xs text-destructive">{validationError}</p>}
    </div>
  );
}

function HexColorInput(props: {
  id: string;
  value: string;
  onChange: (next: unknown) => void;
  disabled: boolean;
  className: string;
}): JSX.Element {
  const { id, value, onChange, disabled, className } = props;
  // Accept either a literal hex (#RRGGBB) or an HSL triple ("221 83% 53%")
  // - match BrandEditor's behaviour so existing brand_config-style
  // values round-trip without rewrites.
  const triple = isHslTriple(value);
  const hex = triple ? hslTripleToHex(value) : /^#[0-9a-f]{6}$/i.test(value) ? value : "#000000";
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hex}
        onChange={(e) => onChange(triple ? hexToHslTriple(e.target.value) : e.target.value)}
        disabled={disabled}
        className="h-8 w-10 cursor-pointer rounded border bg-background disabled:cursor-not-allowed disabled:opacity-60"
        aria-label="Color picker"
      />
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        placeholder="#1d4ed8"
        spellCheck={false}
        className={`${className} font-mono`}
      />
    </div>
  );
}

// ---------------------------------------------------------------- //
// value_schema parsers                                             //
// ---------------------------------------------------------------- //

function resolveNumber(schema: Record<string, unknown> | undefined, key: string): number | null {
  if (!schema) return null;
  const v = schema[key];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

function resolveOptions(schema: Record<string, unknown> | undefined): readonly SelectOption[] {
  if (!schema) return [];
  const raw = schema.options;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): SelectOption[] => {
    if (typeof entry === "string") return [{ value: entry, label: entry }];
    if (typeof entry === "object" && entry !== null) {
      const v = (entry as Record<string, unknown>).value;
      const l = (entry as Record<string, unknown>).label;
      if (typeof v === "string") {
        return [{ value: v, label: typeof l === "string" ? l : v }];
      }
    }
    return [];
  });
}
